import type {ReactElement} from 'react';
import {useContext, useEffect, useState} from 'react';
import {VersionHistoryOnboardingPopup} from '@Components/version-history-onboarding-popup/version-history-onboarding-popup';
import {markVersionHistoryOnboardingAsSeen, shouldShowVersionHistoryOnboarding} from '@Libraries/email-template-version-history.library';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {EditorViewport, EmailMakerSaveState} from '@EmailMakerReact/email-maker-react.types';
import {useIsVersionHistoryAccessible} from '@EmailMakerReact/hooks/use-is-version-history-accessible';
import styles from './email-maker-version-history-onboarding-popover.module.scss';

/**
 * One-time version-history onboarding popup, anchored below the save-status badge (its entry point).
 */
export function EmailMakerVersionHistoryOnboardingPopover(): ReactElement | null {
  const {state} = useContext(emailMakerContext);
  const isVersionHistoryAccessible = useIsVersionHistoryAccessible();
  const [isShowing, setIsShowing] = useState(false);

  const shouldAttempt = isVersionHistoryAccessible && state.saveState === EmailMakerSaveState.SAVED && state.editorViewport !== EditorViewport.MOBILE;

  useEffect(() => {
    if (!shouldAttempt) {
      return undefined;
    }

    let isActive = true;
    void shouldShowVersionHistoryOnboarding().then((shouldShow): void => {
      if (!isActive || !shouldShow) {
        return;
      }

      setIsShowing(true);
      void markVersionHistoryOnboardingAsSeen();
    });

    return (): void => {
      isActive = false;
    };
  }, [shouldAttempt]);

  if (!isShowing || state.editorViewport === EditorViewport.MOBILE) {
    return null;
  }

  return (
    <div className={styles.popover}>
      <VersionHistoryOnboardingPopup
        onClose={(): void => {
          setIsShowing(false);
        }}
      />
    </div>
  );
}
